import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Alert, Spinner, Card, Nav, Container, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faDesktop,
  faBell,
  faLock,
  faCreditCard,
  faCircleInfo,
  faCog,
  faTrash,
  faExchangeAlt,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from '../../contexts/AuthContext';
import settingsService from '../../services/settingsService';
import { useNavigate } from 'react-router-dom';
import './Settings.scss';
import { 
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential 
} from 'firebase/auth';
import { auth } from '../../services/firebase';
import { useTranslation } from 'react-i18next';
import Subscription from './Subscription';
import { useLanguage } from '../../contexts/LanguageContext';

const RECEIPT_WIDTHS = [
  { value: '58mm', label: '58mm' },
  { value: '80mm', label: '80mm' },
  { value: '100mm', label: '100mm' },
  { value: '120mm', label: '120mm' },
  { value: '150mm', label: '150mm' },
  { value: '200mm', label: '200mm' }
];

function Settings() {
  const { currentUser: user } = useAuthContext();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
  const [activeTab, setActiveTab] = useState('subscription');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const { t, i18n } = useTranslation();
  const { changeLanguage } = useLanguage();
  const [pendingLanguage, setPendingLanguage] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!user) {
        navigate('/login');
        return;
      }
      try {
        await fetchSettings();
      } catch (error) {
        console.error('Error initializing settings:', error);
        setError(error.message);
      }
    };
    
    checkAuth();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsService.get();
      setSettings(response.data);
      
      if (response.data?.language && response.data.language !== i18n.language) {
        i18n.changeLanguage(response.data.language);
        localStorage.setItem('preferredLanguage', response.data.language);
      }
      
      if (response.data?.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'startingReceiptNumber') {
      const newValue = parseInt(value) || 1;
      setSettings(prev => ({
        ...prev,
        startingReceiptNumber: newValue,
        currentReceiptNumber: Math.max(newValue, prev.currentReceiptNumber || 1)
      }));
    } else {
      const newValue = type === 'checkbox' ? checked : value;
      setSettings(prev => ({
        ...prev,
        [name]: newValue
      }));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        if (file.size > 2 * 1024 * 1024) {
          showAlert(t('settings.messages.logoTooLarge'), 'danger');
          return;
        }
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
          setSettings(prev => ({
            ...prev,
            logo: reader.result
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing logo:', error);
        showAlert('Errore nel caricamento del logo', 'danger');
      }
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setSettings(prev => ({
      ...prev,
      logo: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      showAlert(t('settings.messages.userNotAuthenticated'), 'danger');
      return;
    }

    try {
      setLoading(true);
      const updatedSettings = {
        ...settings,
        receiptWidth: settings.receiptWidth || '58mm',
        currentReceiptNumber: Math.max(
          settings.currentReceiptNumber || 1,
          settings.startingReceiptNumber || 1
        )
      };
      
      await settingsService.update(updatedSettings);
      
      if (pendingLanguage) {
        changeLanguage(pendingLanguage);
        setPendingLanguage(null);
      }
      
      showAlert(t('settings.messages.saveSuccess'), 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showAlert(error.message || t('settings.messages.saveError'), 'danger');
      
      if (pendingLanguage) {
        setSettings(prev => ({
          ...prev,
          language: i18n.language
        }));
        setPendingLanguage(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setSaveStatus({ show: true, message, type });
    setTimeout(() => {
      setSaveStatus({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset degli stati di errore/successo quando l'utente inizia a digitare
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Le nuove password non coincidono');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('La nuova password deve essere di almeno 6 caratteri');
      return;
    }

    try {
      setUpdatingPassword(true);
      setPasswordError('');
      
      // Riautenticazione dell'utente
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordForm.currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      
      // Aggiornamento password
      await updatePassword(user, passwordForm.newPassword);
      
      // Reset form e mostra messaggio di successo
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordSuccess('Password aggiornata con successo');
      
    } catch (error) {
      console.error('Error updating password:', error);
      switch (error.code) {
        case 'auth/wrong-password':
          setPasswordError('La password attuale non è corretta');
          break;
        case 'auth/weak-password':
          setPasswordError('La nuova password è troppo debole');
          break;
        default:
          setPasswordError('Errore nell\'aggiornamento della password');
      }
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleLanguageChange = (language) => {
    setPendingLanguage(language);
    setSettings(prev => ({
      ...prev,
      language: language
    }));
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  if (!user) {
    return (
      <Alert variant="warning">
        Per accedere alle impostazioni devi effettuare il login.
      </Alert>
    );
  }

  return (
    <Container className="settings-container py-4">
      <div className="settings-header mb-4">
        <h1>{t('settings.title')}</h1>
        {saveStatus.show && (
          <Alert 
            variant={saveStatus.type} 
            className="save-alert mt-3"
          >
            {saveStatus.message}
          </Alert>
        )}
      </div>

      <Nav 
        className="settings-nav mb-4"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
      >
        <Nav.Item>
          <Nav.Link 
            eventKey="subscription"
            className={`nav-tab ${activeTab === 'subscription' ? 'active' : ''}`}
          >
            <div className="nav-tab-content">
              <div className="icon-container">
                <FontAwesomeIcon icon={faCreditCard} />
              </div>
              <span>{t('settings.tabs.subscription')}</span>
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="account"
            className={`nav-tab ${activeTab === 'account' ? 'active' : ''}`}
          >
            <div className="nav-tab-content">
              <div className="icon-container">
                <FontAwesomeIcon icon={faLock} />
              </div>
              <span>{t('settings.tabs.account')}</span>
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="company" 
            className={`nav-tab ${activeTab === 'company' ? 'active' : ''}`}
          >
            <div className="nav-tab-content">
              <div className="icon-container">
                <FontAwesomeIcon icon={faDesktop} />
              </div>
              <span>{t('settings.tabs.company')}</span>
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="print"
            className={`nav-tab ${activeTab === 'print' ? 'active' : ''}`}
          >
            <div className="nav-tab-content">
              <div className="icon-container">
                <FontAwesomeIcon icon={faBell} />
              </div>
              <span>{t('settings.tabs.print')}</span>
            </div>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="system"
            className={`nav-tab ${activeTab === 'system' ? 'active' : ''}`}
          >
            <div className="nav-tab-content">
              <div className="icon-container">
                <FontAwesomeIcon icon={faCog} />
              </div>
              <span>{t('settings.tabs.system')}</span>
            </div>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="tab-content">
        {/* Sezione Società */}
        <div className={`tab-pane ${activeTab === 'company' ? 'show active' : ''}`}>
          <Form onSubmit={handleSubmit}>
            <div className="settings-section">
              <h2>{t('settings.company.title')}</h2>
              <p className="text-muted mb-4">{t('settings.company.description')}</p>
              
              <Form.Group className="mb-4">
                <Form.Label>{t('settings.company.companyName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="companyName"
                  value={settings?.companyName}
                  onChange={handleInputChange}
                  placeholder={t('settings.company.companyNamePlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.company.address')}</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={settings?.address}
                  onChange={handleInputChange}
                  placeholder={t('settings.company.addressPlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.company.region')}</Form.Label>
                <Form.Control
                  type="text"
                  name="region"
                  value={settings?.region}
                  onChange={handleInputChange}
                  placeholder={t('settings.company.regionPlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.company.phone')}</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={settings?.phone}
                  onChange={handleInputChange}
                  placeholder={t('settings.company.phonePlaceholder')}
                />
              </Form.Group>
            </div>

            <div className="settings-section mt-4">
              <h2>{t('settings.company.logo')}</h2>
              <p className="text-muted mb-4">{t('settings.company.logoDescription')}</p>
              
              <div className="logo-upload-section">
                {logoPreview ? (
                  <div className="logo-preview-container">
                    <div className="logo-preview mt-3">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="img-fluid"
                      />
                    </div>
                    <div className="logo-actions mt-3 d-flex gap-2">
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={handleRemoveLogo}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        {t('common.remove')}
                      </Button>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                        {t('common.change')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline-secondary" 
                    className="upload-btn"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    {t('settings.company.uploadLogo')}
                  </Button>
                )}
                <Form.Control
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="d-none"
                />
              </div>
            </div>

            <div className="settings-section mt-4">
              <h2>{t('settings.company.warranty')}</h2>
              <p className="text-muted mb-4">{t('settings.company.warrantyDescription')}</p>
              
              <Form.Group>
                <Form.Label>{t('settings.company.warrantyDays')}</Form.Label>
                <Form.Control
                  type="number"
                  name="warrantyDays"
                  value={settings?.warrantyDays}
                  onChange={handleInputChange}
                  min="0"
                  placeholder={t('settings.company.warrantyDaysPlaceholder')}
                />
              </Form.Group>
            </div>

            <div className="settings-section mt-4">
              <h2>{t('settings.company.disclaimer')}</h2>
              <p className="text-muted mb-4">{t('settings.company.disclaimerDescription')}</p>
              
              <Form.Control
                as="textarea"
                rows={4}
                name="disclaimer"
                value={settings?.disclaimer}
                onChange={handleInputChange}
                placeholder={t('settings.company.disclaimerPlaceholder')}
              />
            </div>
          </Form>
        </div>

        {/* Sezione Stampa */}
        <div className={`tab-pane ${activeTab === 'print' ? 'show active' : ''}`}>
          <Form onSubmit={handleSubmit}>
            <div className="settings-section">
              <h2>{t('settings.print.title')}</h2>
              <p className="text-muted mb-4">{t('settings.print.description')}</p>
              
              <Form.Group className="mb-4">
                <Form.Label>{t('settings.print.printerType')}</Form.Label>
                <Form.Select
                  name="printerType"
                  value={settings?.printerType || 'thermal'}
                  onChange={handleInputChange}
                >
                  <option value="thermal">{t('settings.print.printerTypeThermal')}</option>
                  <option value="a4">{t('settings.print.printerTypeA4')}</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {t('settings.print.printerTypeDescription')}
                </Form.Text>
              </Form.Group>

              {settings?.printerType === 'thermal' && (
                <Form.Group className="mb-4">
                  <Form.Label>{t('settings.print.receiptWidth')}</Form.Label>
                  <Form.Select
                    name="receiptWidth"
                    value={settings?.receiptWidth}
                    onChange={handleInputChange}
                  >
                    {RECEIPT_WIDTHS.map(width => (
                      <option key={width.value} value={width.value}>
                        {width.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {settings?.printerType === 'a4' && (
                <Form.Group className="mb-4">
                  <Form.Label>{t('settings.print.printDuplicate')}</Form.Label>
                  <Form.Check
                    type="switch"
                    id="print-duplicate-switch"
                    name="printDuplicate"
                    checked={settings?.printDuplicate}
                    onChange={handleInputChange}
                    className="custom-switch mt-2"
                    label={t('settings.print.printDuplicateDescription')}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.print.showVAT')}</Form.Label>
                <Form.Check
                  type="switch"
                  id="show-vat-switch"
                  name="showVAT"
                  checked={settings?.showVAT}
                  onChange={handleInputChange}
                  className="custom-switch mt-2"
                  label={t('settings.print.showVATDescription')}
                />
              </Form.Group>

              {settings?.showVAT && (
                <Form.Group className="mb-4">
                  <Form.Label>{t('settings.print.vatPercentage')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="vatPercentage"
                    value={settings?.vatPercentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    placeholder={t('settings.print.vatPercentagePlaceholder')}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.print.startingReceiptNumber')}</Form.Label>
                <Form.Control
                  type="number"
                  name="startingReceiptNumber"
                  value={settings?.startingReceiptNumber}
                  onChange={handleInputChange}
                  min="1"
                />
              </Form.Group>

              <div className="auto-print-setting mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{t('settings.print.autoPrint')}</h5>
                    <p className="text-muted mb-0">{t('settings.print.autoPrintDescription')}</p>
                  </div>
                  <Form.Check
                    type="switch"
                    id="auto-print-switch"
                    name="autoPrint"
                    checked={settings?.autoPrint}
                    onChange={handleInputChange}
                    className="custom-switch"
                  />
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label>{t('settings.print.receiptLanguage.label')}</Form.Label>
                <Form.Select
                  name="receiptLanguage"
                  value={settings?.receiptLanguage || 'system'}
                  onChange={handleInputChange}
                >
                  <option value="system">{t('settings.print.receiptLanguage.system')}</option>
                  <option value="it">{t('settings.print.receiptLanguage.it')}</option>
                  <option value="en">{t('settings.print.receiptLanguage.en')}</option>
                  <option value="zh">{t('settings.print.receiptLanguage.zh')}</option>
                  <option value="es">{t('settings.print.receiptLanguage.es')}</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {t('settings.print.receiptLanguage.description')}
                </Form.Text>
              </Form.Group>
            </div>
          </Form>
        </div>

        {/* Sezione Account */}
        <div className={`tab-pane ${activeTab === 'account' ? 'show active' : ''}`}>
          <div className="settings-section">
            <h2>{t('settings.account.title')}</h2>
            <p className="text-muted mb-4">{t('settings.account.description')}</p>

            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">{t('settings.account.email')}</h5>
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    <div className="mb-2">
                      <strong>{user?.email}</strong>
                    </div>
                    <Badge bg="success">{t('settings.account.verified')}</Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <h5 className="mb-3">{t('settings.account.password')}</h5>
                {passwordError && (
                  <Alert variant="danger" className="mb-3">
                    {passwordError}
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert variant="success" className="mb-3">
                    {passwordSuccess}
                  </Alert>
                )}
                <Form onSubmit={handlePasswordUpdate}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('settings.account.currentPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('settings.account.currentPasswordPlaceholder')}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('settings.account.newPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('settings.account.newPasswordPlaceholder')}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('settings.account.confirmPassword')}</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('settings.account.confirmPasswordPlaceholder')}
                      required
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={updatingPassword}
                    >
                      {updatingPassword ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {t('settings.account.updating')}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faLock} className="me-2" />
                          {t('settings.account.updatePassword')}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Sezione Abbonamento */}
        <div className={`tab-pane ${activeTab === 'subscription' ? 'show active' : ''}`}>
          <Subscription />
        </div>

        {/* Sistema Tab */}
        <div className={`tab-pane ${activeTab === 'system' ? 'show active' : ''}`}>
          <Form onSubmit={handleSubmit}>
            <div className="settings-section">
              <h2>{t('settings.language.title')}</h2>
              <p className="text-muted mb-4">{t('settings.language.select')}</p>
              
              <Form.Group className="mb-4">
                <Form.Label>{t('settings.language.label')}</Form.Label>
                <Form.Select
                  name="language"
                  value={settings?.language || 'it'}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className={`mb-3 ${pendingLanguage ? 'border-primary' : ''}`}
                >
                  <option value="it">{t('settings.language.it')}</option>
                  <option value="en">{t('settings.language.en')}</option>
                  <option value="zh">{t('settings.language.zh')}</option>
                  <option value="es">{t('settings.language.es')}</option>
                </Form.Select>
                {pendingLanguage && (
                  <p className="text-primary">
                    <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
                    {t('settings.language.pendingChange')}
                  </p>
                )}
              </Form.Group>
            </div>

            <div className="settings-section mt-4">
              <h2>{t('settings.currency.title')}</h2>
              <p className="text-muted mb-4">{t('settings.currency.description')}</p>
              
              <Form.Group className="mb-4">
                <Form.Label>{t('settings.currency.label')}</Form.Label>
                <Form.Select
                  name="currency"
                  value={settings?.currency || 'EUR'}
                  onChange={handleInputChange}
                  className="mb-3"
                >
                  <option value="EUR">{t('settings.currency.options.EUR')}</option>
                  <option value="USD">{t('settings.currency.options.USD')}</option>
                  <option value="GBP">{t('settings.currency.options.GBP')}</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button 
                onClick={handleSubmit}
                variant="primary" 
                className="save-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('settings.saving')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {t('settings.save')}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {(activeTab === 'company' || activeTab === 'print') && (
        <div className="d-flex justify-content-end mt-4">
          <Button 
            onClick={handleSubmit}
            variant="primary" 
            className="save-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('settings.save')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {t('settings.save')}
              </>
            )}
          </Button>
        </div>
      )}
    </Container>
  );
}

export default Settings; 